import { useEffect, useState } from 'react'
import type { OverflowWindowInfo } from '@/types/api'
import { padZero } from '@/utils/formatters'
import { getTimeRemaining } from '@/utils/timer'
import { useResolvedOverflowWindow } from '@/hooks/useOverflowWindow'

interface OverflowUnlockTimerProps {
  /** When provided (e.g. share link), skips the API poll and uses this payload. */
  window?: OverflowWindowInfo | null
  className?: string
}

/** Header countdown — locked: time until overflow opens; open: time until daily reset. */
export function OverflowUnlockTimer({ window: windowProp, className = '' }: OverflowUnlockTimerProps) {
  // Only poll when the parent did not supply window state (share link already embeds it).
  const polled = useResolvedOverflowWindow(windowProp == null)
  const w = windowProp ?? polled
  const locked = w.overflow_locked
  const target = locked ? new Date(w.opens_at_utc) : new Date(w.resets_at_utc)

  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const rem = getTimeRemaining(target)
  const timeStr = `${padZero(rem.hours)}:${padZero(rem.minutes)}:${padZero(rem.seconds)}`

  return (
    <div
      className={
        'flex items-center gap-2 px-3 py-1.5 rounded-lg border ' +
        (locked
          ? 'bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30'
          : 'bg-brand-blue/5 border-brand-blue/20 dark:bg-brand-blue/10 dark:border-brand-blue/30') +
        (className ? ` ${className}` : '')
      }
    >
      <span className="text-xs text-text-muted font-medium">
        {locked ? 'Overflow unlock' : 'Overflow open · reset'}
      </span>
      <span
        className={
          'text-sm font-mono font-semibold tabular-nums ' +
          (locked ? 'text-amber-600 dark:text-amber-400' : 'text-brand-blue')
        }
      >
        {timeStr}
      </span>
    </div>
  )
}

export const OVERFLOW_QUALITY_WARNING =
  'You are within 3 hours of the daily cap reset. Traffic started this late may not mature in quality metrics — requesting overflow now puts you at risk of poor quality scores.'
